import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Spinner } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import ShellCard from "components/ShellCard";
import SectionCard from "components/SectionCard";
import Row, { Badge } from "components/ChecklistRow";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useToast } from "hooks/toast";
import { getPainelHabilitacao, reenviarHomologacao } from "services/habilitacaoApi";
import { formatDocument } from "utils/formatDoc";

import ManualDoPortalButton from "components/ManualDoPortalButton";
import ContatoTiForm from "./ContatoTiForm";
import CadastroEstabelecimentoForm, { resumoReceita } from "components/CadastroEstabelecimentoForm";
import { getCadastro, atualizarEstabelecimento, atualizarPelaReceita, mensagemErro } from "services/meuFreteApi";
import { enviarLogoMeuFrete, removerLogoMeuFrete, mensagemErroLogo } from "services/transportadoraLogoApi";
import { estadoPainel, descreverFaltando } from "./estadoPainel";

// Esteira da transportadora por embarcador: Cadastro, Integração CT-e e
// Homologação, com o estado vindo do backend (retomável). Relê ao montar, ao
// voltar o foco e no botão Atualizar.
//
// Nasceu como painel de BLOQUEIO (PO1-3106), onde a identidade provisória
// aterrissava até a Everlog aprovar. Em 20/09/2026 virou o item "Embarcadores"
// do menu do MeuFrete: quem tem login entra no portal, e a esteira é
// informação. Por isso a tela não redireciona mais quando a transportadora
// deixa de estar em habilitação.

const hintStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const subListStyle = { marginTop: "0.6rem", display: "flex", flexDirection: "column", gap: "0.45rem" };
const subItemStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.75rem",
  padding: "0.5rem 0.7rem",
  borderRadius: 8,
  background: AUTOPILOT_COLORS.insetSurface,
  fontSize: "0.84rem",
  color: AUTOPILOT_COLORS.text,
};
// Coluna da direita de toda sub-linha, SEMPRE na mesma ordem: estado e depois
// ação. Antes cada linha fazia à sua maneira — "Dados da empresa" tinha só o
// botão, "Contato de TI" tinha badge depois botão, e a integração tinha badge
// depois um texto solto. Três padrões na mesma lista, e o olho não achava a
// coluna de status.
const acoesStyle = { display: "flex", alignItems: "center", gap: "0.5rem", flex: "none" };

export default function Habilitacao() {
  const { addToast } = useToast();
  const [painel, setPainel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [abertoTi, setAbertoTi] = useState(null);
  const [reenviando, setReenviando] = useState(null);
  // PO1-2308 (decisão 3): a transportadora é dona do cadastro desde o aceite; o
  // mesmo formulário do MeuFrete abre aqui, no card Cadastro.
  const [editandoEmpresa, setEditandoEmpresa] = useState(false);
  const [matriz, setMatriz] = useState(null);
  const [salvandoEmpresa, setSalvandoEmpresa] = useState(false);

  const abrirEdicaoEmpresa = async () => {
    try {
      const c = await getCadastro();
      setMatriz(c.matriz);
      setEditandoEmpresa(true);
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível abrir o cadastro", description: mensagemErro(e) });
    }
  };

  const salvarEmpresa = async (dados) => {
    if (!matriz) return;
    setSalvandoEmpresa(true);
    try {
      await atualizarEstabelecimento(matriz.id, dados);
      addToast({ place: "tr", type: "success", title: "Cadastro atualizado" });
      setEditandoEmpresa(false);
      carregar();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível salvar", description: mensagemErro(e) });
    } finally {
      setSalvandoEmpresa(false);
    }
  };

  // Logo (PO1-2722): mesma ação do MeuFrete > Cadastro, aqui no card do painel.
  const [logoBusy, setLogoBusy] = useState(false);
  const enviarLogoEmpresa = async (blob, nome) => {
    if (!matriz) return;
    setLogoBusy(true);
    try {
      await enviarLogoMeuFrete(matriz.id, blob, nome);
      addToast({ place: "tr", type: "success", title: "Logo atualizado" });
      const c = await getCadastro();
      setMatriz(c.matriz);
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível enviar o logo", description: mensagemErroLogo(e) });
    } finally {
      setLogoBusy(false);
    }
  };
  const removerLogoEmpresa = async () => {
    if (!matriz) return;
    setLogoBusy(true);
    try {
      await removerLogoMeuFrete(matriz.id);
      addToast({ place: "tr", type: "success", title: "Logo removido" });
      const c = await getCadastro();
      setMatriz(c.matriz);
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível remover o logo", description: mensagemErroLogo(e) });
    } finally {
      setLogoBusy(false);
    }
  };

  const receitaEmpresa = async () => {
    if (!matriz) return;
    try {
      const out = await atualizarPelaReceita(matriz.id);
      addToast({ place: "tr", type: "success", title: "Receita consultada", description: resumoReceita(out) });
      const c = await getCadastro();
      setMatriz(c.matriz);
      carregar();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível consultar a Receita", description: mensagemErro(e) });
    }
  };

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      const dados = await getPainelHabilitacao();
      setPainel(dados);
      // PO1-3166 (20/09/2026): NÃO redireciona mais, e não relê mais o /users/me.
      // Esta tela virou o item "Embarcadores" do menu, um destino permanente —
      // antes ela era o painel de bloqueio e, ao ver `emHabilitacao === false`,
      // chamava `refreshUser` e saía sozinha para a operação. Com a esteira fora
      // do bloqueio, esse desvio expulsava a transportadora da própria tela: ela
      // clicava em Embarcadores, via "Carregando o painel…" e caía em Minhas
      // faturas. O `refreshUser` foi junto porque `user.emHabilitacao` não
      // decide mais nada: o layout usa `false` fixo e `rotaInicial` o ignora.
    } catch (e) {
      setErro((e && e.response && e.response.data && e.response.data.error) || "Não foi possível carregar o painel.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
    const onFocus = () => carregar();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [carregar]);

  const estado = useMemo(() => (painel ? estadoPainel(painel) : null), [painel]);

  const reenviar = async (idVinculo) => {
    setReenviando(idVinculo);
    try {
      await reenviarHomologacao(idVinculo);
      addToast({ place: "tr", type: "success", title: "Enviado para reavaliação", description: "A Everlog vai analisar de novo." });
      carregar();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível reenviar", description: (e && e.response && e.response.data && e.response.data.error) || "Tente de novo." });
    } finally {
      setReenviando(null);
    }
  };

  const transportadora = painel && painel.transportadora;

  return (
    <div className="content">
      <PageHero
        eyebrow="Portal da transportadora"
        title="Embarcadores"
        description="Onde está cada embarcador que você atende. Quem ainda está entrando aparece com as etapas pendentes; o portal já está liberado."
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <ManualDoPortalButton />
            <PageHeroRefreshButton onClick={carregar} loading={loading} />
          </div>
        }
      />

      <ShellCard>
        {loading && !painel ? (
          <div style={{ ...hintStyle, display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem" }}>
            <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
            <span>Carregando o painel…</span>
          </div>
        ) : erro ? (
          <div style={{ ...hintStyle, padding: "1rem 0.25rem" }} role="alert">
            {erro}{" "}
            <Button size="sm" color="info" outline onClick={carregar}>
              Tentar novamente
            </Button>
          </div>
        ) : estado ? (
          <SectionCard
            title={transportadora ? transportadora.fantasia || transportadora.razaoSocial : "Transportadora"}
            description={transportadora ? `CNPJ ${formatDocument(transportadora.cnpj)}` : ""}
            action={<Badge tipo={estado.macro === "OPERACIONAL" ? "done" : estado.macro === "EM_HOMOLOGACAO" || estado.macro === "EM_ONBOARDING" ? "auto" : "pend"}>{estado.macroLabel}</Badge>}
          >
            <p style={{ ...hintStyle, marginBottom: 18 }}>
              {estado.concluidos} de {estado.total} etapas concluídas
              {painel.receitaEmAndamento
                ? " · Estamos completando seus dados pela Receita Federal. Se continuar pendente, a Everlog completa na homologação."
                : ""}
            </p>

            {estado.semVinculo && (
              <p style={hintStyle}>Nenhum embarcador com vínculo ativo no momento. Quando um embarcador te convidar, as etapas aparecem aqui.</p>
            )}

            {/* 1. Cadastro */}
            <Row numero={1} titulo={estado.cards[0].titulo} sub={estado.cards[0].sub} done={estado.cards[0].done} badge={estado.cards[0].badge} badgeTipo={estado.cards[0].badgeTipo}>
              <div style={{ ...subItemStyle, flexDirection: "column", alignItems: "stretch", marginTop: "0.6rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem" }}>
                  <span>
                    Dados da empresa
                    {estado.dados.ok ? null : (
                      <span style={hintStyle}> · faltam {descreverFaltando(estado.dados.faltando) || "dados"}</span>
                    )}
                  </span>
                  <span style={acoesStyle}>
                    <Badge tipo={estado.dados.ok ? "done" : "pend"}>{estado.dados.ok ? "Completo" : "Incompleto"}</Badge>
                    <Button size="sm" color="info" outline onClick={() => (editandoEmpresa ? setEditandoEmpresa(false) : abrirEdicaoEmpresa())}>
                      {editandoEmpresa ? "Fechar" : "Editar"}
                    </Button>
                  </span>
                </div>
                {editandoEmpresa && matriz && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <CadastroEstabelecimentoForm
                      estabelecimento={matriz}
                      compact
                      salvando={salvandoEmpresa}
                      onSalvar={salvarEmpresa}
                      onReceita={receitaEmpresa}
                      onCancelar={() => setEditandoEmpresa(false)}
                      onEnviarLogo={enviarLogoEmpresa}
                      onRemoverLogo={removerLogoEmpresa}
                      logoBusy={logoBusy}
                    />
                  </div>
                )}
              </div>
              <div style={subListStyle}>
                {estado.contatosTi.map((c) => (
                  <div key={c.idVinculo} style={{ ...subItemStyle, flexDirection: "column", alignItems: "stretch" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem" }}>
                      <span>
                        Contato de TI para <strong style={{ color: AUTOPILOT_COLORS.title }}>{c.embarcador && c.embarcador.nome}</strong>
                        {c.ok && c.contato ? (
                          <span style={hintStyle}>
                            {" "}· {c.contato.nome} &lt;{c.contato.email}&gt;
                          </span>
                        ) : null}
                      </span>
                      <span style={acoesStyle}>
                        <Badge tipo={c.ok ? "done" : "pend"}>{c.ok ? "Cadastrado" : "Pendente"}</Badge>
                        <Button size="sm" color="info" outline onClick={() => setAbertoTi(abertoTi === c.idVinculo ? null : c.idVinculo)}>
                          {abertoTi === c.idVinculo ? "Fechar" : c.ok ? "Alterar" : "Cadastrar"}
                        </Button>
                      </span>
                    </div>
                    {abertoTi === c.idVinculo && (
                      <ContatoTiForm
                        idVinculo={c.idVinculo}
                        embarcadorNome={c.embarcador && c.embarcador.nome}
                        sugestao={painel.sugestaoContato}
                        contatoAtual={c.contato}
                        onSalvo={() => {
                          setAbertoTi(null);
                          carregar();
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </Row>

            {/* 2. Integração CT-e */}
            <Row numero={2} titulo={estado.cards[1].titulo} sub={estado.cards[1].sub} done={estado.cards[1].done} badge={estado.cards[1].badge} badgeTipo={estado.cards[1].badgeTipo}>
              <div style={subListStyle}>
                {estado.integracoes.map((i) => (
                  <div key={i.idVinculo} style={subItemStyle}>
                    <span>
                      <strong style={{ color: AUTOPILOT_COLORS.title }}>{i.embarcador && i.embarcador.nome}</strong>
                      <span style={hintStyle}>
                        {" "}· {i.ok ? `Conectado via ${i.origem === "SFTP" ? "pasta SFTP" : "conector"}` : i.emOnboarding ? "Canal sendo montado pela Everlog" : "Sem canal de CT-e ativo"}
                        {i.ok ? (i.primeiroCte ? " · primeiro CT-e processado" : " · aguardando o primeiro CT-e") : ""}
                      </span>
                    </span>
                    <span style={acoesStyle}>
                      <Badge tipo={i.ok ? "done" : i.emOnboarding ? "auto" : "pend"}>{i.ok ? "Conectado" : i.emOnboarding ? "Em onboarding" : "Pendente"}</Badge>
                    </span>
                  </div>
                ))}
                {/* PO1-3281: o canal é montado no onboarding de cada embarcador, depois da
                    homologação, e não bloqueia mais a aprovação. A credencial fica em
                    Integrações › Acesso SFTP (PO1-2309). */}
                <p style={{ ...hintStyle, margin: "0.15rem 0 0" }}>
                  O canal de CT-e é montado pela Everlog no onboarding de cada embarcador, depois da homologação. A
                  credencial SFTP de cada um fica em Integrações › Acesso SFTP.
                </p>
              </div>
            </Row>

            {/* 3. Homologação */}
            <Row numero={estado.cards[2].done ? "✓" : 3} titulo={estado.cards[2].titulo} sub={estado.cards[2].sub} done={estado.cards[2].done} locked={false} badge={estado.cards[2].badge} badgeTipo={estado.cards[2].badgeTipo}>
              <div style={subListStyle}>
                {estado.homologacoes.map((h) => (
                  <div key={h.idVinculo} style={{ ...subItemStyle, flexDirection: "column", alignItems: "stretch" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem" }}>
                      <span>
                        <strong style={{ color: AUTOPILOT_COLORS.title }}>{h.embarcador && h.embarcador.nome}</strong>
                      </span>
                      <span style={acoesStyle}>
                        {h.devolvido || h.faseLabel !== estado.macroLabel ? (
                          <Badge tipo={h.homologado ? "done" : h.prontoParaHomologar || h.emOnboarding ? "auto" : h.devolvido ? "pend" : "lock"}>
                            {h.devolvido ? "Devolvido" : h.faseLabel}
                          </Badge>
                        ) : (
                          <span style={hintStyle}>aguardando as etapas acima</span>
                        )}
                      </span>
                    </div>
                    {h.devolvido && (
                      <div style={{ marginTop: "0.5rem" }}>
                        <div style={{ ...hintStyle, color: AUTOPILOT_COLORS.statusWarning }}>Motivo da Everlog: {h.motivoDevolucao}</div>
                        <Button size="sm" color="primary" style={{ marginTop: "0.4rem" }} disabled={reenviando === h.idVinculo} onClick={() => reenviar(h.idVinculo)}>
                          {reenviando === h.idVinculo ? <Spinner size="sm" /> : "Corrigi, enviar de novo"}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Row>

            <p style={{ ...hintStyle, marginTop: 14 }}>
              A homologação é feita pela <b>Everlog</b>, uma vez por transportadora. Depois dela, cada embarcador novo entra direto em onboarding (pastas, usuário SFTP e portal).
            </p>
          </SectionCard>
        ) : null}
      </ShellCard>
    </div>
  );
}
